import React, { useState } from 'react'
import { 
  User, 
  Award, 
  BookOpen, 
  Target,
  Star,
  CheckCircle,
  XCircle,
  Clock
} from 'lucide-react'
import CandidateSkillScore from './CandidateSkillScore'

interface PositionSkill {
  skill_id: number
  skill_name: string
  category: string
  proficiency_level: 'beginner' | 'intermediate' | 'advanced' | 'expert'
  is_good_to_have?: boolean
}

interface PositionCertification {
  certification_name: string
  certification_body?: string
  is_required: boolean
  expiry_required?: boolean
}

interface TimelineEvent {
  type: string
  name: string
  date: string | null
  notes: string | null
  status: string
  byId: number | null
  byName: string | null
  metadata: any
}

interface Candidate {
  id: number
  candidate_uuid: string
  first_name: string
  last_name: string
  full_name: string
  email: string
  phone?: string
  current_title?: string
  current_company?: string
  location?: string
  skills?: string[] | any
  application_status?: string
  timeline?: TimelineEvent[]
  applied_at?: string
  last_updated?: string
}

interface CandidateDetailsProps {
  candidate: Candidate
  positionSkills: PositionSkill[]
  positionCertifications: PositionCertification[]
  isExpanded: boolean
  isLoading: boolean
  timeline: TimelineEvent[]
}

export default function CandidateDetails({ 
  candidate, 
  positionSkills, 
  positionCertifications, 
  isExpanded, 
  isLoading, 
  timeline 
}: CandidateDetailsProps) {
  const [activeSection, setActiveSection] = useState<'skills' | 'certifications' | 'timeline'>('skills')

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'applied': return <Clock className="w-4 h-4" />
      case 'shortlisted': return <CheckCircle className="w-4 h-4" />
      case 'hired': return <Star className="w-4 h-4" />
      case 'rejected': return <XCircle className="w-4 h-4" />
      default: return <Clock className="w-4 h-4" />
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'applied': return 'bg-blue-100 text-blue-800'
      case 'shortlisted': return 'bg-green-100 text-green-800'
      case 'hired': return 'bg-emerald-100 text-emerald-800'
      case 'rejected': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="px-6 py-4 bg-gray-50">
      <div className="border border-gray-200 rounded-lg p-4 bg-white">
        {/* Header with Score */}
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-sm font-medium text-gray-900 flex items-center">
            <User className="w-4 h-4 mr-2" />
            Candidate Details
          </h4>
          <CandidateSkillScore 
            candidateSkills={candidate.skills || []}
            positionSkills={positionSkills || []}
          />
        </div>

        {/* Section Tabs */}
        <div className="flex space-x-4 mb-4 border-b border-gray-200">
          <button
            onClick={() => setActiveSection('skills')}
            className={`pb-2 px-1 text-sm font-medium border-b-2 ${
              activeSection === 'skills'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Target className="w-4 h-4 inline mr-1" />
            Skills Match
          </button>
          <button
            onClick={() => setActiveSection('certifications')}
            className={`pb-2 px-1 text-sm font-medium border-b-2 ${
              activeSection === 'certifications'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Award className="w-4 h-4 inline mr-1" />
            Certifications
          </button>
          <button
            onClick={() => setActiveSection('timeline')}
            className={`pb-2 px-1 text-sm font-medium border-b-2 ${
              activeSection === 'timeline'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <BookOpen className="w-4 h-4 inline mr-1" />
            Timeline
          </button>
        </div>

        {/* Section Content */}
        {isLoading ? (
          <div className="flex items-center justify-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-sm text-gray-600">Loading details...</span>
          </div>
        ) : (
          <div className="min-h-[200px]">
            {/* Skills Match Section */}
            {activeSection === 'skills' && (
              <div className="space-y-4">
                {/* Candidate Skills */}
                <div>
                  <h5 className="text-sm font-medium text-gray-700 mb-2">Candidate Skills</h5>
                  <div className="flex flex-wrap gap-2">
                    {Array.isArray(candidate.skills) ? candidate.skills.map((skill, index) => {
                      const skillName = typeof skill === 'string' 
                        ? skill 
                        : skill.skillName || skill.skill_name || skill.name;
                      const isPrimary = skill.isPrimarySkill || false;
                      const proficiency = skill.proficiencyLevel || '';
                      
                      return (
                        <span
                          key={skill.uuid || index}
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            isPrimary 
                              ? 'bg-green-100 text-green-800 border border-green-200' 
                              : 'bg-blue-100 text-blue-800'
                          }`}
                          title={proficiency ? `${skillName} - ${proficiency}` : skillName}
                        >
                          {skillName}
                          {isPrimary && <span className="ml-1">⭐</span>}
                        </span>
                      );
                    }) : (
                      <span className="text-sm text-gray-500">No skills listed</span>
                    )}
                  </div>
                </div>

                {/* Position Requirements */}
                <div>
                  <h5 className="text-sm font-medium text-gray-700 mb-2">Position Requirements</h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Required Skills */}
                    <div>
                      <h6 className="text-xs font-medium text-red-700 mb-2">Required Skills</h6>
                      <div className="space-y-1">
                        {positionSkills?.filter(skill => !skill.is_good_to_have).map((skill, index) => (
                          <div key={index} className="flex items-center justify-between text-xs">
                            <span>{skill.skill_name}</span>
                            <span className="text-gray-500">{skill.proficiency_level}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Good-to-Have Skills */}
                    <div>
                      <h6 className="text-xs font-medium text-green-700 mb-2">Good-to-Have Skills</h6>
                      <div className="space-y-1">
                        {positionSkills?.filter(skill => skill.is_good_to_have).map((skill, index) => (
                          <div key={index} className="flex items-center justify-between text-xs">
                            <span>{skill.skill_name}</span>
                            <span className="text-gray-500">{skill.proficiency_level}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Certifications Section */}
            {activeSection === 'certifications' && (
              <div className="space-y-4">
                <div>
                  <h5 className="text-sm font-medium text-gray-700 mb-2">Position Certifications</h5>
                  {positionCertifications && positionCertifications.length > 0 ? (
                    <div className="space-y-2">
                      {positionCertifications.map((cert, index) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                          <div>
                            <span className="text-sm font-medium">{cert.certification_name}</span>
                            {cert.certification_body && (
                              <span className="text-xs text-gray-500 ml-2">({cert.certification_body})</span>
                            )}
                          </div>
                          <span className={`text-xs px-2 py-1 rounded ${
                            cert.is_required 
                              ? 'bg-red-100 text-red-800' 
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {cert.is_required ? 'Required' : 'Preferred'}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span className="text-sm text-gray-500">No certifications required for this position</span>
                  )}
                </div>
              </div>
            )}

            {/* Timeline Section */}
            {activeSection === 'timeline' && (
              <div className="space-y-4">
                {timeline.length > 0 ? (
                  <div className="space-y-3">
                    {timeline.map((event, index) => (
                      <div key={index} className="flex items-start space-x-3">
                        <div className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                        <div className="flex-grow min-w-0">
                          <div className="text-sm font-medium text-gray-900">
                            {event.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {event.date ? new Date(event.date).toLocaleDateString() : 'No date'}
                            {event.byName && ` • by ${event.byName}`}
                          </div>
                          {event.notes && (
                            <div className="text-sm text-gray-600 mt-1">
                              {event.notes}
                            </div>
                          )}
                        </div>
                        <div className="flex-shrink-0">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(event.status)}`}>
                            {getStatusIcon(event.status)}
                            <span className="ml-1">{event.status}</span>
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4 text-sm text-gray-500">
                    No timeline events found for this candidate.
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}